import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Volume2, Eye, RotateCcw, Check, Loader2, Brain, ArrowLeft, CircleCheck, CircleDot } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { playAppAudio } from "@/lib/appAudio";
import { srDisplay } from "@/lib/serbianScript";
import { langDisplayName } from "@/lib/languages";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import AppLayout from "@/components/AppLayout";

const SIZES = [5, 10, 20, 0]; // 0 = all currently due

export default function Review() {
  const { user, refresh } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const pid = user?.active_learning_profile_id;
  const [q, setQ] = useState(null); // queue response
  const [size, setSize] = useState(10);
  const [cards, setCards] = useState(null); // active session
  const [i, setI] = useState(0);
  const [results, setResults] = useState([]);
  const reqId = useRef(0);

  const load = useCallback(async (mode = "due") => {
    const id = ++reqId.current; // only the latest request may fill the screen
    setQ(null); setCards(null); setI(0); setResults([]);
    try {
      const r = await api.get("/review/queue", { params: { limit: 0, mode } });
      if (id === reqId.current) { setQ(r.data); setSize(r.data.cards.length <= 10 ? 0 : 10); }
    } catch { if (id === reqId.current) setQ({ cards: [], due_total: 0, error: true }); }
  }, []);
  const profileChanged = useCallback(async () => { await refresh(); load(); }, [refresh, load]);
  useEffect(() => { load(); }, [load, pid]); // profile switch discards the old queue immediately

  const start = (list) => { setCards(list); setI(0); setResults([]); };
  const disp = (s) => (q?.target_lang === "sr" ? srDisplay(s, user?.target_script) : s);

  if (!q) return <Shell><div className="nb-card p-8 text-center" data-testid="review-loading"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div></Shell>;

  if (cards && i >= cards.length) return <Shell><Summary results={results} onToday={() => navigate("/today")} onAgain={() => load()} t={t} /></Shell>;
  if (cards) {
    return (
      <Shell>
        <div className="text-xs font-bold text-gray-500 mb-2" data-testid="review-progress">{t("review.progress", { n: i + 1, total: cards.length })}</div>
        <div className="flex gap-1 mb-4" aria-hidden>{cards.map((c, k) => <span key={c.vocab_id} className={`h-1.5 flex-1 rounded-full border border-ink ${k < i ? "bg-ink" : "bg-white"}`} />)}</div>
        <ReviewCard key={cards[i].vocab_id} card={cards[i]} profileId={q.profile_id} targetLang={q.target_lang} disp={disp} t={t}
          onRated={(r) => { setResults((x) => [...x, r]); setI((n) => n + 1); }} onProfileChanged={profileChanged} />
      </Shell>
    );
  }

  const due = q.cards;
  const pick = size === 0 ? due : due.slice(0, size);
  return (
    <Shell>
      {due.length === 0 ? (
        <div className="nb-card p-8 text-center" data-testid="review-empty">
          <Check className="w-10 h-10 mx-auto stroke-[3]" />
          <h2 className="font-cabinet font-extrabold text-2xl mt-2">{t("review.caughtUp")}</h2>
          <p className="text-gray-600 mt-1">{t("review.nothingDue")}</p>
          {q.mode === "due" && <button onClick={() => load("recent")} className="nb-btn-outline mt-6 px-6 py-3" data-testid="review-practice-recent">{t("review.practiceRecent")}</button>}
        </div>
      ) : (
        <div className="nb-card p-6" data-testid="review-start">
          <p className="text-lg" data-testid="review-due-count">{q.mode === "due" ? t("review.dueCount", { count: q.due_total }) : t("review.recentCount", { count: due.length })}</p>
          <div className="flex gap-2 mt-4 flex-wrap" role="radiogroup">
            {SIZES.filter((s) => s === 0 || s < due.length).map((s) => (
              <button key={s} onClick={() => setSize(s)} data-testid={`review-size-${s || "all"}`}
                className={`nb-tag ${size === s ? "bg-ink text-white" : "bg-white"}`}>{s ? s : t(q.mode === "due" ? "review.allDue" : "review.all")}</button>
            ))}
          </div>
          <button onClick={() => start(pick)} className="nb-btn-primary w-full mt-6 py-4 text-lg" data-testid="review-start-btn">{t("review.start", { count: pick.length })}</button>
        </div>
      )}
    </Shell>
  );
}

function Shell({ children }) {
  const { t } = useI18n();
  return (
    <AppLayout>
      <div className="max-w-xl mx-auto px-4 py-8" data-testid="review-page">
        <div className="flex items-center gap-2 mb-1"><Brain className="w-6 h-6 text-coral" /><h1 className="font-cabinet font-extrabold text-3xl">{t("review.title")}</h1></div>
        <p className="text-gray-600 mb-6">{t("review.tagline")}</p>
        {children}
      </div>
    </AppLayout>
  );
}

function ReviewCard({ card, profileId, targetLang, disp, t, onRated, onProfileChanged }) {
  const [revealed, setRevealed] = useState(false);
  const [typed, setTyped] = useState(null); // {answer, correct} after Check; {skipped} after Show answer
  const [busy, setBusy] = useState(false);
  const [noAudio, setNoAudio] = useState(false);
  const sent = useRef(false);
  const type = card.type === "listening" && noAudio ? "recognition" : card.type;
  // Fetch failure → no voice for this card; a blocked autoplay keeps the tap-to-play button.
  const listen = () => playAppAudio(`/vocab/${card.vocab_id}/audio`).catch((e) => { if (e?.name === "NotAllowedError") return; setNoAudio(true); toast.error(t("review.voiceUnavailable")); });
  useEffect(() => { if (card.type === "listening") listen(); }, []); // eslint-disable-line

  const rate = async (result) => {
    if (sent.current) return; // double-tap guard (server also ignores repeats)
    sent.current = true; setBusy(true);
    try {
      const r = await api.post(`/review/cards/${card.vocab_id}/rate`, { result, seen_due: card.seen_due, profile_id: profileId });
      onRated({ ok: result === "got_it", days: r.data.interval_days });
    } catch (e) {
      if (e?.response?.status === 409) { onProfileChanged(); return; } // profile switched elsewhere: drop this queue
      sent.current = false; setBusy(false); toast.error(t("review.saveFailed"));
    }
  };

  const front = {
    recognition: <Big testid="review-front">{disp(card.word)}</Big>,
    recall: <Big testid="review-front" plain>{card.translation}</Big>,
    context: <p className="font-cabinet font-bold text-2xl leading-snug" dir="auto" data-testid="review-front">{disp(card.cloze || "")}</p>,
    typed: <TypedFront card={card} profileId={profileId} targetLang={targetLang} t={t} done={revealed} onProfileChanged={onProfileChanged}
      onResult={(r) => { setTyped(r); setRevealed(true); }} />,
    listening: <button onClick={listen} className="w-20 h-20 mx-auto bg-sky border-2 border-ink rounded-full shadow-hard flex items-center justify-center" data-testid="review-listen-front" aria-label={t("review.listen")}><Volume2 className="w-9 h-9" /></button>,
  }[type];
  const back = type === "typed" ? <TypedBack typed={typed} word={disp(card.word)} t={t} /> : type === "recall" || type === "context" || type === "listening"
    ? <><Big testid="review-answer">{disp(card.word)}</Big>{type !== "recall" && card.translation && <p className="text-gray-600 mt-1" dir="auto">{card.translation}</p>}</>
    : <Big testid="review-answer" plain>{card.translation || disp(card.example)}</Big>;

  return (
    <div className="nb-card p-6 sm:p-8 min-h-[22rem] flex flex-col" data-testid={`review-card-${type}`}>
      <div className="text-xs font-bold uppercase tracking-widest rtl:tracking-normal text-gray-500" data-testid="review-type">{t(`review.type.${type}`)}</div>
      <div className="flex-1 flex flex-col justify-center text-center py-6">
        {front}
        {revealed && <div className="mt-6 pt-6 border-t-2 border-ink/20" data-testid="review-reveal-area">
          {back}
          {card.can_listen && !noAudio && type !== "listening" && <button onClick={listen} className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-coral" data-testid="review-listen"><Volume2 className="w-4 h-4" /> {t("review.listen")}</button>}
        </div>}
      </div>
      {!revealed ? (type === "typed" ? null :
        <button onClick={() => setRevealed(true)} className="nb-btn-outline w-full py-4 text-lg" data-testid="review-reveal"><Eye className="w-5 h-5" /> {t("review.reveal")}</button>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <button onClick={() => rate("again")} disabled={busy} className={`${typed && !typed.correct ? "nb-btn-primary" : "nb-btn-outline"} py-4 text-lg disabled:opacity-60`} data-testid="review-again"><RotateCcw className="w-5 h-5" /> {t("review.again")}</button>
          <button onClick={() => rate("got_it")} disabled={busy} className="nb-btn-mint py-4 text-lg disabled:opacity-60" data-testid="review-got-it"><Check className="w-5 h-5" /> {t("review.gotIt")}</button>
        </div>
      )}
    </div>
  );
}

function TypedFront({ card, profileId, targetLang, t, done, onResult, onProfileChanged }) {
  const { locale } = useI18n();
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus({ preventScroll: true }); }, []);
  const check = async (e) => {
    e.preventDefault();
    const a = answer.trim();
    if (!a || busy || done) return;
    setBusy(true);
    try {
      const r = await api.post(`/review/cards/${card.vocab_id}/check`, { answer: a, seen_due: card.seen_due, profile_id: profileId });
      onResult({ answer: a, correct: r.data.correct });
    } catch (err) {
      if (err?.response?.status === 409) { onProfileChanged(); return; }
      setBusy(false); toast.error(t("review.typed.checkFailed"));
    }
  };
  return (
    <div>
      <Big testid="review-front" plain>{card.translation}</Big>
      {!done && <form onSubmit={check} className="mt-6 text-start" data-testid="typed-form">
        <label htmlFor="typed-input" className="text-sm font-bold">{t("review.typed.label")}</label>
        <input id="typed-input" ref={inputRef} value={answer} onChange={(e) => setAnswer(e.target.value)} dir="auto" lang={targetLang}
          autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} maxLength={200}
          placeholder={t("review.typed.placeholder", { lang: langDisplayName(targetLang, locale) })}
          className="mt-1 w-full border-2 border-ink rounded-xl px-4 py-4 text-xl bg-white focus:outline-none focus:ring-4 focus:ring-coral/30" data-testid="typed-input" />
        <button type="submit" disabled={!answer.trim() || busy} className="nb-btn-primary w-full mt-3 py-4 text-lg disabled:opacity-50" data-testid="typed-check">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />} {t("review.typed.check")}</button>
        <button type="button" onClick={() => onResult({ skipped: true })} className="block mx-auto mt-3 text-sm font-bold underline text-gray-600" data-testid="typed-show-answer">{t("review.typed.showAnswer")}</button>
      </form>}
    </div>
  );
}

function TypedBack({ typed, word, t }) {
  const ok = typed?.correct;
  return (
    <div role="status" aria-live="polite" data-testid="typed-result">
      {!typed?.skipped && <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border-2 border-ink font-bold ${ok ? "bg-mint" : "bg-butter"}`} data-testid={ok ? "typed-correct" : "typed-not-quite"}>
        {ok ? <CircleCheck className="w-4 h-4" /> : <CircleDot className="w-4 h-4" />} {t(ok ? "review.typed.correct" : "review.typed.notQuite")}</div>}
      <div className="text-xs font-bold uppercase tracking-widest rtl:tracking-normal text-gray-500 mt-4">{t("review.typed.answer")}</div>
      <Big testid="review-answer">{word}</Big>
      {!typed?.skipped && !ok && <p className="mt-2 text-gray-700" data-testid="typed-your-answer">{t("review.typed.yourAnswer")}: <bdi className="font-bold">{typed?.answer}</bdi></p>}
      {!ok && <p className="mt-3 text-sm text-gray-600" data-testid="typed-suggest-again">{t("review.typed.suggestAgain")}</p>}
    </div>
  );
}

function Big({ children, testid, plain }) {
  return <div className={`font-cabinet font-extrabold ${plain ? "text-2xl" : "text-4xl"} break-words`} dir="auto" data-testid={testid}>{children}</div>;
}

function Summary({ results, onToday, onAgain, t }) {
  const ok = results.filter((r) => r.ok).length;
  const days = results.map((r) => r.days).filter(Boolean);
  const next = days.length ? Math.min(...days) : null;
  return (
    <div className="nb-card bg-mint p-6 text-center" data-testid="review-summary">
      <Check className="w-10 h-10 mx-auto stroke-[3]" />
      <h2 className="font-cabinet font-extrabold text-2xl mt-2">{t("review.complete")}</h2>
      <div className="grid grid-cols-3 gap-2 mt-5 text-sm">
        <Stat n={results.length} label={t("review.reviewed")} testid="review-sum-reviewed" />
        <Stat n={ok} label={t("review.remembered")} testid="review-sum-remembered" />
        <Stat n={results.length - ok} label={t("review.anotherLook")} testid="review-sum-again" />
      </div>
      {next && <p className="text-sm mt-4" data-testid="review-next">{t("review.nextIn", { count: next })}</p>}
      <div className="flex gap-3 mt-6">
        <button onClick={onAgain} className="nb-btn-outline flex-1 py-3" data-testid="review-back-start">{t("review.done")}</button>
        <button onClick={onToday} className="nb-btn-primary flex-1 py-3" data-testid="review-back-today"><ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {t("review.backToday")}</button>
      </div>
    </div>
  );
}

function Stat({ n, label, testid }) {
  return <div className="bg-white border-2 border-ink rounded-xl p-3"><div className="font-cabinet font-extrabold text-2xl" data-testid={testid}>{n}</div><div className="text-xs text-gray-600">{label}</div></div>;
}
